/**
 * CauseTreemap: rectangles sized by count, one per category, drawn on Recharts
 * (the Wave C catalog's Treemap). Used for the findings by Ishikawa 6M root
 * cause distribution; the click-through contract carries the category through
 * the mark's own click.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { ResponsiveContainer, Tooltip, Treemap } from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, seriesColor } from "./shared";

/** One treemap entry: the category label and the count that sizes it. */
export interface CauseDatum {
  readonly name: string;
  readonly value: number;
  readonly [key: string]: unknown;
}

interface TileProps {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  readonly index?: number;
  readonly name?: string;
  readonly value?: number | string;
}

function Tile({
  x,
  y,
  width,
  height,
  index,
  name,
  value,
  onSelect,
}: TileProps & {
  readonly onSelect?: ((datum: CauseDatum, index: number) => void) | undefined;
}): React.JSX.Element | null {
  if (x === undefined || y === undefined || width === undefined || height === undefined) {
    return null;
  }
  const label = name ?? "";
  const count = typeof value === "number" ? value : Number(value ?? 0);
  const clickable =
    onSelect === undefined || label.length === 0
      ? undefined
      : () => {
          onSelect({ name: label, value: count }, index ?? 0);
        };
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={6}
        ry={6}
        fill={seriesColor(index ?? 0)}
        stroke="var(--q-panel)"
        className={clickable === undefined ? undefined : "q-chart__hit"}
        tabIndex={clickable === undefined ? undefined : 0}
        role="img"
        aria-label={strings.charts.mark(label, count)}
        onClick={clickable}
        onKeyDown={
          clickable === undefined
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  clickable();
                }
              }
        }
      >
        <title>{strings.charts.mark(label, count)}</title>
      </rect>
      {width > 56 && height > 32 && (
        <text x={x + 8} y={y + 20} fill="var(--q-ink)" fontSize={12} pointerEvents="none">
          {label}
        </text>
      )}
    </g>
  );
}

export function CauseTreemap({
  ariaLabel,
  data,
  onSelectDatum,
}: {
  ariaLabel: string;
  data: readonly CauseDatum[];
  /** Optional click-through (the chart contract): one callback per tile. */
  onSelectDatum?: (datum: CauseDatum, index: number) => void;
}): React.JSX.Element {
  const total = data.reduce((sum, datum) => sum + datum.value, 0);
  if (data.length === 0 || total === 0) {
    return <ChartEmpty />;
  }
  return (
    <figure
      className="q-chart q-chart--treemap"
      data-testid="cause-treemap"
      role="img"
      aria-label={ariaLabel}
    >
      <div className="q-chart__frame">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <Treemap
            data={[...data]}
            dataKey="value"
            nameKey="name"
            stroke="var(--q-panel)"
            isAnimationActive={false}
            content={(props: TileProps) => <Tile {...props} onSelect={onSelectDatum} />}
          >
            <Tooltip
              isAnimationActive={false}
              content={({ active: hover, payload }) => {
                const row = payload[0]?.payload as Record<string, unknown> | undefined;
                if (!hover || row === undefined) {
                  return null;
                }
                const name = typeof row.name === "string" ? row.name : "";
                const value = typeof row.value === "number" ? row.value : Number(row.value ?? 0);
                return (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{strings.charts.mark(name, value)}</p>
                  </div>
                );
              }}
            />
          </Treemap>
        </ResponsiveContainer>
      </div>
    </figure>
  );
}
